import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPriceFR } from '../utils/formatFR';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modale pour affecter un bien à un contact (depuis la fiche contact).
 * Recherche dans le portefeuille puis POST /contacts/{id}/biens-presentes
 */
const AffectPropertyToContactModal = ({ contactId, currentUserId, onClose, onSuccess }) => {
  const [search, setSearch] = useState('');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Récupère le négociateur courant (depuis prop OU localStorage)
  const resolvedUserId = React.useMemo(() => {
    if (currentUserId) return currentUserId;
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u?.id || null;
    } catch {
      return null;
    }
  }, [currentUserId]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = {};
        if (search) params.q = search;
        const res = await axios.get(`${API}/properties`, { params });
        if (!cancelled) {
          const list = Array.isArray(res.data) ? res.data : (res.data?.properties || []);
          setProperties(list.slice(0, 20));
        }
      } catch {
        if (!cancelled) setProperties([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  const handleSubmit = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts/${contactId}/biens-presentes`, {
        bien_id: selectedId,
        statut: 'sélectionné',
        negociateur_id: resolvedUserId,
      });
      toast.success('Bien affecté au contact');
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'affectation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      data-testid="affect-property-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-800">Affecter un bien au contact</h3>
            <p className="text-xs text-slate-500">Sélectionnez le bien à proposer</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (référence, titre, ville…)"
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
              data-testid="affect-property-search"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="text-center py-8 text-sm text-slate-500"><i className="bi bi-arrow-clockwise animate-spin mr-2"></i>Recherche…</div>
          ) : properties.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500">Aucun bien trouvé</div>
          ) : (
            <ul className="space-y-1">
              {properties.map((p) => (
                <li key={p.id}>
                  <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border ${
                    selectedId === p.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                  }`}>
                    <input
                      type="radio"
                      name="property"
                      checked={selectedId === p.id}
                      onChange={() => setSelectedId(p.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">
                        {p.bien_general?.titre_annonce || p.titre_annonce || p.titre || `Bien ${p.reference || ''}`.trim() || 'Sans titre'}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        <span className="font-bold">{p.reference}</span>
                        {(p.bien_general?.ville || p.localisation?.ville) && <> · {p.bien_general?.ville || p.localisation?.ville}</>}
                        {(p.informations_vente?.prix_cession || p.prix_vente) && <> · <span className="num">{formatPriceFR(p.informations_vente?.prix_cession || p.prix_vente)}</span></>}
                      </p>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-white rounded-lg">Annuler</button>
          <button
            onClick={handleSubmit}
            disabled={!selectedId || submitting}
            className="px-4 py-2 bg-[#0062CC] hover:bg-[#0056b3] disabled:bg-slate-300 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
            data-testid="affect-property-submit"
          >
            <i className="bi bi-check-lg"></i>{submitting ? 'Affectation…' : 'Affecter le bien'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AffectPropertyToContactModal;
